import React, {
	useState,
	useCallback,
	createContext,
	useContext,
	useMemo
} from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ConfirmationDialogProps {
	title: string;
	description: string;
	isOpen: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
	title,
	description,
	isOpen,
	onConfirm,
	onCancel,
	confirmLabel = 'Delete',
	cancelLabel = 'Cancel',
	confirmVariant = 'destructive'
}) => {
	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
			<DialogContent className="sm:max-w-[425px] dark:bg-gray-900 dark:border-gray-700 gap-6">
				<DialogHeader>
					<DialogTitle className="text-destructive">
						{title}
					</DialogTitle>
					<DialogDescription className="text-muted-foreground dark:text-gray-300 pt-1">
						{description}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter className="gap-4">
					<Button
						type="button"
						variant={confirmVariant}
						onClick={onConfirm}
						className="order-2 sm:order-none"
					>
						{confirmLabel}
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						className="order-1 sm:order-none"
					>
						{cancelLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

interface DialogState {
	isOpen: boolean;
	title: string;
	description: string;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

interface ConfirmationContextType {
	showDialog: (
		title: string,
		description: string,
		options?: {
			confirmLabel?: string;
			cancelLabel?: string;
			confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
		}
	) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

// Provider component
export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => {
	const [dialogState, setDialogState] = useState<DialogState>({
		isOpen: false,
		title: '',
		description: '',
		confirmLabel: undefined,
		confirmVariant: 'destructive'
	});
	const [resolver, setResolver] = useState<((value: boolean) => void) | null>(
		null
	);

	const showDialog = useCallback(
		(
			title: string,
			description: string,
			options?: {
				confirmLabel?: string;
				cancelLabel?: string;
				confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
			}
		): Promise<boolean> => {
			return new Promise(resolve => {
				setResolver(() => resolve);
				setDialogState({
					isOpen: true,
					title,
					description,
					confirmLabel: options?.confirmLabel,
					cancelLabel: options?.cancelLabel,
					confirmVariant: options?.confirmVariant
				});
			});
		},
		[]
	);

	const handleConfirm = useCallback(() => {
		setDialogState(prev => ({ ...prev, isOpen: false }));
		if (resolver) {
			resolver(true);
			setResolver(null);
		}
	}, [resolver]);

	const handleCancel = useCallback(() => {
		setDialogState(prev => ({ ...prev, isOpen: false }));
		if (resolver) {
			resolver(false);
			setResolver(null);
		}
	}, [resolver]);

	const contextValue = useMemo(() => ({ showDialog }), [showDialog]);

	return (
		<ConfirmationContext.Provider value={contextValue}>
			{children}
			<ConfirmationDialog
				{...dialogState}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>
		</ConfirmationContext.Provider>
	);
};

// Hook to use the confirmation dialog
export const useConfirmationDialog = () => {
	const context = useContext(ConfirmationContext);
	if (!context) {
		throw new Error(
			'useConfirmationDialog must be used within a ConfirmationProvider'
		);
	}
	return context;
};

// Standalone function for backwards compatibility
export const confirmationDialog = (
	title: string,
	description: string
): Promise<boolean> => {
	// Fallback to native confirm wrapped in a Promise
	return Promise.resolve(globalThis.confirm(`${title}\n\n${description}`));
};
